import { Css, GridTable, actionColumn, column, emptyCell, simpleHeader } from "@homebound/beam";
import { useMemo } from "react";
import { createMilestoneDetailsPageUrl } from "src/RouteUrls";
import { ProgressPill, formatDate } from "src/components";
import { MilestoneActionCell } from "src/routes/projects/dynamic-schedules/draft-mode/DraftScheduleTable";
import { ScheduleDraftMode_PlanMilestoneFragment } from "src/routes/projects/dynamic-schedules/draft-mode/ScheduleDraftMode.generated";
import { pluralize } from "src/utils";
import { ScheduleDateVariance } from "../components/ScheduleVariance";
import { ScheduleSnapshotsHookResult, useScheduleSnapshots } from "../components/useScheduleSnapshots";

type DynamicSchedulesMilestoneTableProps = {
  planMilestones: ScheduleDraftMode_PlanMilestoneFragment[];
  projectId: string;
  hasDraftChanges: boolean;
};

// TODO: Move into components or draft dir or wherever we decide given the size of this refactor
export function DynamicSchedulesMilestoneTable({
  planMilestones,
  projectId,
  hasDraftChanges,
}: DynamicSchedulesMilestoneTableProps) {
  const { getPlanMilestoneSnapshot } = useScheduleSnapshots(projectId);
  const columns = useMemo(
    () => createColumns(getPlanMilestoneSnapshot, hasDraftChanges),
    [getPlanMilestoneSnapshot, hasDraftChanges],
  );
  const rows = useMemo(() => createRows(planMilestones), [planMilestones]);

  return (
    <div css={Css.w100.h("calc(100vh - 160px)").$}>
      <GridTable
        as="virtual"
        columns={columns}
        rows={rows}
        sorting={{ on: "client", initial: ["startDate", "ASC"] }}
        rowStyles={{
          header: { cellCss: Css.bgGray100.$ },
          data: { rowLink: (row) => createMilestoneDetailsPageUrl(projectId, row.data.id) },
        }}
      />
    </div>
  );
}

type HeaderRow = { kind: "header" };
type DataRow = { kind: "data"; data: ScheduleDraftMode_PlanMilestoneFragment };

export type Row = HeaderRow | DataRow;

function createColumns(
  getPlanMilestoneSnapshot: ScheduleSnapshotsHookResult["getPlanMilestoneSnapshot"],
  hasDraftChanges: boolean,
) {
  return [
    column<Row>({
      id: "name",
      header: { content: "Milestones", css: Css.lg.jcfs.gray900.pl0.aife.$ },
      data: ({ name }) => name,
      mw: "120px",
      clientSideSort: true,
    }),
    column<Row>({
      id: "startDate",
      header: "Est. Start",
      data: ({ estStartDate }) => ({
        content: () => formatDate(estStartDate?.date, "monthShort"),
        value: estStartDate?.date,
      }),
      w: "120px",
      clientSideSort: true,
    }),
    column<Row>({
      id: "endDate",
      header: "Est. End",
      data: ({ estEndDate, id }) => ({
        content: () => (
          <>
            {formatDate(estEndDate?.date, "monthShort")}
            <ScheduleDateVariance current={estEndDate} baseline={getPlanMilestoneSnapshot(id)?.estEndDate} />
          </>
        ),
        value: formatDate(estEndDate?.date, "monthShort"),
        sortValue: estEndDate?.date,
      }),
      w: "140px",
      clientSideSort: true,
    }),
    column<Row>({
      id: "progress",
      header: "Progress",
      data: ({ progress }) => ({
        content: () => <ProgressPill changeColorOnCompleted progress={progress} />,
        value: progress,
      }),
      w: "180px",
      clientSideSort: true,
    }),
    column<Row>({
      id: "duration",
      header: "Duration",
      data: ({ durationInDays }) => ({
        content: () => (
          <span>
            {durationInDays} {pluralize(durationInDays, "day", "days")}
          </span>
        ),
        sortValue: durationInDays,
      }),
      w: "90px",
      clientSideSort: true,
    }),
    actionColumn<Row>({
      id: "actions",
      header: emptyCell,
      data: (row) => <MilestoneActionCell milestone={row} hasDraftChanges={hasDraftChanges} />,
      w: "48px",
      clientSideSort: false,
    }),
  ];
}

function createRows(data: ScheduleDraftMode_PlanMilestoneFragment[]) {
  return [simpleHeader, ...data.map((pm) => ({ id: pm.id, kind: "data" as const, data: pm }))];
}
